import React from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withTranslation } from 'react-i18next'
import { cloneDeep, isEqual, set } from 'lodash'
import { Redirect, Route, Switch } from 'react-router-dom'

import AssetManager from '../../common/components/AssetManager/AssetManager'
import Toast from '../../common/components/Toast/Toast'
import ConfirmationDialog from '../../common/components/Modal/ConfirmationDialog/ConfirmationDialog'
import GAMIFICATION_ACTION_TYPES from '../../common/constants/GamificationActionTypes'
import PROJECT_TYPES from '../../common/constants/ProjectTypes'
import { encodeStringsInObject, decodeStringsInObject } from '../../common/utils/objectStringsAction.js'

import { API__PROJECTS, API__TEMPLATES } from '../../api'

import { getProjectEmbedCodeWithIFrame, getProjectEmbedCodeWithLoader } from '../../utils/embed'
import getBlocksFromStructure from '../../utils/getBlocksFromStructure.js'

import TopBar from './TopBar/TopBar'

import EditorTab from './Tabs/Editor'
import PreviewTab from './Tabs/Preview'
import IntegrationsTab from './Tabs/Integrations'
import SharingOptionsTab from './Tabs/SharingOptions'
import { BLOCK_DICTIONARY } from './Tabs/Editor/Blocks/BlockSettings'

import girlSitting from './i/girlSitting.svg'
import imageLoading from './i/preloader.gif'

import './index.scss'

const CONFIRMATION_DIALOG_TYPES = {
    emptyProject: 'emptyProject',
    emptyHomePage: 'emptyHomePage',
    premiumBlocks: 'premiumBlocks',
}
const AUTO_SAVE_INTERVAL = 20000 // 1000 = 1s

class Editor extends React.Component {
    autoSaveIntervalId

    constructor(props) {
        super(props)
        this.state = {
            project: null,
            tmpProject: null,

            isReady: false,

            processes: {
                autoSaving: false,
                saving: false,
                publishing: false,
            },

            assetManager: {
                isOpen: false,
                placeImageCallback: () => {
                    /**/
                },
                settings: undefined,
            },

            confirmationDialog: {
                isOpen: false,
                type: undefined, // publish, etc.
                payload: {},
            },
        }
    }

    async componentDidMount() {
        const { history } = this.props
        try {
            const { match } = this.props

            let project

            if (match.params.projectId) {
                project = await API__PROJECTS.GET_PROJECT(match.params.projectId)
            } else {
                history.push('/')
                return
            }

            await this.setState({
                project,
                tmpProject: {
                    ...project,
                    projectStructureJson: this.getDecodedObject(cloneDeep(project.projectStructureJson)),
                },
                isReady: true,
            })

            this.autoSaveIntervalId = setInterval(this.autoSave, AUTO_SAVE_INTERVAL)
        } catch (err) {
            console.error(err)
            Toast('error', { message: 'Cannot get project' })
            history.push('/account/my-projects')
        }
    }

    componentWillUnmount() {
        clearInterval(this.autoSaveIntervalId)
    }

    /**
     * Get encoded object
     * @param {object,null} obj
     */
    getEncodedObject = (obj = null) => {
        if (obj) {
            return encodeStringsInObject(obj)
        }
        const {
            tmpProject: { projectStructureJson },
        } = this.state
        return encodeStringsInObject(cloneDeep(projectStructureJson))
    }

    /**
     * Get decoded object
     * @param {object,null} obj
     */
    getDecodedObject = (obj = null) => {
        if (obj) {
            return decodeStringsInObject(obj)
        }
        const {
            tmpProject: { projectStructureJson },
        } = this.state
        return decodeStringsInObject(cloneDeep(projectStructureJson))
    }

    /**
     * Auto-saving
     */
    autoSave = async () => {
        const { project, tmpProject } = this.state

        const projectToCheck = {}
        const tmpProjectToCheck = {}
        const fieldsToCheck = ['name', 'title', 'description']
        fieldsToCheck.forEach(el => {
            projectToCheck[el] = project[el]
            tmpProjectToCheck[el] = tmpProject[el]
        })
        projectToCheck['projectStructureJson'] = project['projectStructureJson']
        tmpProjectToCheck['projectStructureJson'] = this.getEncodedObject()

        if (!isEqual(projectToCheck, tmpProjectToCheck)) {
            const { processes } = this.state
            if (!processes.autoSaving && !processes.saving && !processes.publishing) {
                console.log('Changes detected! Initialized auto-saving')
                try {
                    await this.setState(prevState => ({
                        processes: {
                            ...prevState.processes,
                            autoSaving: true,
                        },
                    }))

                    await this.updateProject(false)
                } catch (err) {
                    console.error(err)
                    Toast('error', {
                        title: 'Autosaving error',
                        message: 'An error occurred while trying to autosave project',
                    })
                } finally {
                    await this.setState(prevState => ({
                        processes: {
                            ...prevState.processes,
                            autoSaving: false,
                        },
                    }))
                }
            }
        }
    }

    updateProject = async (setProcess = true) => {
        try {
            if (setProcess) {
                await this.setState(prevState => ({
                    processes: {
                        ...prevState.processes,
                        saving: true,
                    },
                }))
            }

            const { tmpProject } = this.state

            const updatedProject = await API__PROJECTS.UPDATE_PROJECT(tmpProject.projectId, {
                id: tmpProject.id,
                projectId: tmpProject.projectId,
                projectType: {
                    id: tmpProject.projectType.id,
                },
                projectStructureJson: this.getEncodedObject(),
                projectActions: tmpProject.projectActions,
                projectForms: tmpProject.projectForms,
                name: tmpProject.name.trim(),
                title: tmpProject.title.trim(),
                description: tmpProject.description.trim(),
                customImageSelected: tmpProject.customImageSelected,
            })

            await this.setState(prevState => ({
                project: updatedProject,
                tmpProject: {
                    ...prevState.tmpProject,
                    // Эти поля меняются только на сервере (список не полный, но конкрено эти нужно обновить в "tmpProject")
                    imageUrl: updatedProject.imageUrl,
                    customUrl: updatedProject.customUrl,
                },
            }))
        } catch (err) {
            console.error(err)
            throw err
        } finally {
            if (setProcess) {
                await this.setState(prevState => ({
                    processes: {
                        ...prevState.processes,
                        saving: false,
                    },
                }))
            }
        }
    }

    publishProject = async (force = false) => {
        const {
            project: { projectId },
        } = this.state
        const { history } = this.props

        await this.setState(prevState => ({
            processes: {
                ...prevState.processes,
                publishing: true,
            },
        }))

        if (!force && !(await this.checkIsCanUserPublish())) {
            return
        }

        await this.updateProject(false)
        await API__PROJECTS.PUBLISH(projectId)
        await this.sendConstraintsGamificationBlocks()
        const publishedProject = await API__PROJECTS.GET_PROJECT(projectId)

        const projectVersion = publishedProject.projectVersions[0]
        const hashOfProject = projectVersion.versionHash
        const iframeCode =
            publishedProject.projectType.projectType === PROJECT_TYPES.blockArticle
                ? getProjectEmbedCodeWithIFrame(hashOfProject)
                : ''

        history.push('/account/my-projects?tab=published', {
            action: 'editor--published',
            note: 'changes',
            data: {
                projectUrl: projectVersion.publicUrl,
                embedCode: getProjectEmbedCodeWithLoader(hashOfProject),
                iframeCode: iframeCode,
            },
        })
    }

    checkIsCanUserPublish = async () => {
        const {
            tmpProject: { projectStructureJson },
        } = this.state

        const { userSubscription, userDetails } = this.props
        const currentProductName = userSubscription.product.name.split(' ')[0]

        const allBlocks = getBlocksFromStructure(projectStructureJson)

        if (projectStructureJson.hasOwnProperty('pages')) {
            const homePageHasBlocks = projectStructureJson.pages[0].blocks.length
            if (!homePageHasBlocks) {
                this.showConfirmationDialog(CONFIRMATION_DIALOG_TYPES.emptyHomePage, {})
                return false
            }
        }

        if (!allBlocks.length) {
            this.showConfirmationDialog(CONFIRMATION_DIALOG_TYPES.emptyProject, {})
            return false
        }

        if (!userDetails.isTest && currentProductName === 'Free') {
            const premiumBlocksIds = await API__TEMPLATES.GET_PREMIUM_BLOCKS()
            const selectedPremiumBlocks = []
            allBlocks.forEach(el => {
                if (premiumBlocksIds.includes(el.t)) selectedPremiumBlocks.push(BLOCK_DICTIONARY[el.t])
            })
            if (selectedPremiumBlocks.length) {
                this.showConfirmationDialog(CONFIRMATION_DIALOG_TYPES.premiumBlocks, {
                    blocks: selectedPremiumBlocks,
                })
                return false
            }
        }
        return true
    }

    sendConstraintsGamificationBlocks = async () => {
        const {
            project: { id },
            tmpProject: { projectStructureJson },
        } = this.state

        const allBlocks = getBlocksFromStructure(projectStructureJson)
        const blocksWithGamification = allBlocks.filter(el => {
            return Object.keys(GAMIFICATION_ACTION_TYPES).includes(el.t.toString())
        })
        for (const block of blocksWithGamification) {
            if (block.isEnableRating) {
                if (block.numberOfAttempts) {
                    await API__PROJECTS.SEND_CONSTRAINT(GAMIFICATION_ACTION_TYPES[block.t].finish, id, block.id, {
                        value: block.numberOfAttempts,
                    })
                } else {
                    await API__PROJECTS.REMOVE_CONSTRAINT(GAMIFICATION_ACTION_TYPES[block.t].finish, id, block.id)
                }
            }
        }
    }

    /**
     * Change temporary project
     * @param {string} target
     * @param {any} value
     */
    changeProject = async (target, value) => {
        try {
            const { tmpProject } = this.state

            const _tmpProject = cloneDeep(tmpProject)

            set(_tmpProject, target, value)

            await this.setState({
                tmpProject: _tmpProject,
            })
        } catch (err) {
            console.error(err)
        }
    }

    /**
     * Open AssetManager
     * @param {func} cb
     * @param {object} settings
     */
    openAssetManager = (cb, settings) => {
        this.setState(prevState => ({
            assetManager: {
                ...prevState.assetManager,
                isOpen: true,
                placeImageCallback: cb,
                settings: settings,
            },
        }))
    }

    /**
     * Close AssetManager
     */
    closeAssetManager = () => {
        this.setState({
            assetManager: {
                isOpen: false,
                placeImageCallback: () => {
                    /**/
                },
                settings: undefined,
            },
        })
    }

    showConfirmationDialog = (type, payload) => {
        this.setState({
            confirmationDialog: {
                isOpen: true,
                type,
                payload,
            },
        })
    }
    closeConfirmationDialog = () => {
        this.setState({
            confirmationDialog: {
                isOpen: false,
                type: undefined,
                payload: {},
            },
        })
    }

    render() {
        const { history, i18n } = this.props
        const { isReady, tmpProject, assetManager, processes, confirmationDialog } = this.state

        return (
            <div className="editor-new" style={{ pointerEvents: processes.publishing ? 'none' : 'auto' }}>
                {isReady ? (
                    <>
                        <TopBar
                            data={{
                                processes,
                                project: tmpProject,
                            }}
                            history={history}
                            methods={{
                                save: this.updateProject,
                                changeProject: this.changeProject,
                                publishProject: this.publishProject,
                            }}
                        />
                        <Switch>
                            <Route
                                exact
                                path="/editor/:projectId"
                                render={() => (
                                    <EditorTab
                                        data={{
                                            project: tmpProject,
                                        }}
                                        methods={{
                                            getEncodedObject: this.getEncodedObject,
                                            getDecodedObject: this.getDecodedObject,
                                            openAssetManager: this.openAssetManager,
                                            changeProject: this.changeProject,
                                        }}
                                    />
                                )}
                            />
                            <Route
                                exact
                                path="/editor/:projectId/preview"
                                render={() => (
                                    <PreviewTab
                                        data={{
                                            project: tmpProject,
                                        }}
                                    />
                                )}
                            />
                            <Route
                                exact
                                path="/editor/:projectId/integrations"
                                render={() => (
                                    <IntegrationsTab
                                        data={{
                                            projectId: tmpProject.projectId,
                                            integrations: tmpProject.projectStructureJson.integrations,
                                        }}
                                        methods={{
                                            changeProject: this.changeProject,
                                        }}
                                    />
                                )}
                            />
                            <Route
                                exact
                                path="/editor/:projectId/sharing-options"
                                render={() => (
                                    <SharingOptionsTab
                                        data={{
                                            project: tmpProject,
                                        }}
                                        history={history}
                                        methods={{
                                            save: this.updateProject,
                                            openAssetManager: this.openAssetManager,
                                            changeProject: this.changeProject,
                                        }}
                                    />
                                )}
                            />

                            <Redirect from="*" to="/" />
                        </Switch>

                        <AssetManager
                            isOpen={assetManager.isOpen}
                            projectId={tmpProject.projectId}
                            onPlaceImage={assetManager.placeImageCallback}
                            settings={assetManager.settings}
                            onClose={this.closeAssetManager}
                        />
                    </>
                ) : (
                    <div className="loading">
                        <img src={imageLoading} alt="loading" />
                    </div>
                )}

                {confirmationDialog.isOpen && confirmationDialog.type === CONFIRMATION_DIALOG_TYPES.emptyHomePage ? (
                    <ConfirmationDialog
                        onClose={() => this.closeConfirmationDialog()}
                        onAction={() => this.publishProject(true) && this.closeConfirmationDialog()}
                        data={{
                            headText: i18n.t("You haven't created any block in home page"),
                            noteText: i18n.t('You can still publish the project, but it will be empty'),
                            actionVariant: 'primary',
                            actionText: i18n.t('Publish'),
                            cancelText: i18n.t('Cancel'),
                        }}
                    />
                ) : null}
                {confirmationDialog.isOpen && confirmationDialog.type === CONFIRMATION_DIALOG_TYPES.emptyProject ? (
                    <ConfirmationDialog
                        onClose={() => this.closeConfirmationDialog()}
                        onAction={() => this.publishProject(true) && this.closeConfirmationDialog()}
                        data={{
                            headText: i18n.t("You haven't created any block"),
                            noteText: i18n.t('You can still publish the project, but it will be empty'),
                            actionVariant: 'primary',
                            actionText: i18n.t('Publish'),
                            cancelText: i18n.t('Cancel'),
                        }}
                    />
                ) : null}
                {confirmationDialog.isOpen && confirmationDialog.type === CONFIRMATION_DIALOG_TYPES.premiumBlocks ? (
                    <ConfirmationDialog
                        onClose={() => this.closeConfirmationDialog()}
                        onAction={async () => {
                            await this.autoSave()
                            history.push('/account/billing-and-payments?tab=subscription')
                        }}
                        data={{
                            headText: i18n.t('Publication not available!'),
                            noteText: (
                                <>
                                    <p>
                                        {i18n.t('Unable to publish the following blocks: ')}
                                        {confirmationDialog.payload.blocks.map((block, ind) => (
                                            <span key={block.t} style={{ fontWeight: 'bold' }}>
                                                {block.label}
                                                {ind < confirmationDialog.payload.blocks.length - 1 ? ', ' : '.'}
                                            </span>
                                        ))}
                                    </p>
                                    <p>{i18n.t('Your plan needs to be upgraded.')}</p>
                                </>
                            ),
                            actionVariant: 'primary',
                            actionText: i18n.t('Upgrade plan'),
                            cancelText: i18n.t('Close'),
                            image: girlSitting,
                        }}
                    />
                ) : null}
            </div>
        )
    }
}

const mapStateToProps = state => ({
    userDetails: state.user_details,
    userSubscription: state.user_subscription,
})

export default compose(connect(mapStateToProps, null), withTranslation('translations'))(Editor)
